// src/components/themes/default/admin/AddProductModal.tsx
"use client";

import { useState } from "react";
import {
  X,
  Package,
  Upload,
  Sparkles,
  Loader2,
  DollarSign,
  Barcode,
  Layers,
  FileCode,
} from "lucide-react";

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  subdomain: string;
  categories: any[];
  onSuccess: () => void;
  branchId?: number;
  theme?: "dark" | "light";
  primaryColor?: string;
}

export default function AddProductModal({
  isOpen,
  onClose,
  subdomain,
  categories = [],
  onSuccess,
  branchId,
  theme = "dark",
  primaryColor = "#059669",
}: AddProductModalProps) {
  const isLight = theme === "light";

  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [barcode, setBarcode] = useState("");
  const [price, setPrice] = useState("");
  const [comparePrice, setComparePrice] = useState("");
  const [stockQuantity, setStockQuantity] = useState("10");
  const [unit, setUnit] = useState("Piece");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // 🌟 ব্রাঞ্চ ম্যানেজারের ভ্যালিড টোকেন নির্ধারণ
  const getAuthToken = () => {
    if (typeof window === "undefined") return "";

    const currentBranchSlug =
      window.location.pathname.split("/b/")[1]?.split("/")[0]?.split("?")[0] || "";

    if (currentBranchSlug) {
      const branchAuth = localStorage.getItem(`branch_auth_${subdomain}_${currentBranchSlug}`);
      if (branchAuth) {
        try {
          const parsed = JSON.parse(branchAuth);
          if (parsed.token) return parsed.token;
        } catch {}
      }
    }

    return (
      localStorage.getItem("merchant_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("access_token") ||
      ""
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const token = getAuthToken();

      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname.includes("localhost") ||
          window.location.hostname === "127.0.0.1");

      const apiUrl = isLocal
        ? `http://${subdomain}.localhost:8000/api/v1/products/`
        : `/api/v1/products/`;

      const formData = new FormData();
      formData.append("name", name.trim());
      formData.append("sku", sku.trim() || `SKU-${Date.now().toString().slice(-6)}`);
      formData.append("barcode", barcode.trim());
      formData.append("price", price);
      if (comparePrice) formData.append("compare_price", comparePrice);
      formData.append("stock_quantity", stockQuantity);
      formData.append("unit", unit);
      formData.append("description", description.trim());
      formData.append("is_active", "true");

      // 🌟 ক্যাটাগরি আইডি পাঠানো
      if (selectedCategory) {
        formData.append("category_id", selectedCategory);
        formData.append("category", selectedCategory);
      }

      const activeBranch = branchId || localStorage.getItem("active_branch_id");
      if (activeBranch) {
        formData.append("branch_id", String(activeBranch));
      }

      if (imageFile) {
        formData.append("image", imageFile);
      }

      const res = await fetch(apiUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(activeBranch ? { "X-Branch-ID": String(activeBranch) } : {}),
        },
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || data.name?.[0] || data.sku?.[0] || "পণ্য যুক্ত করতে সমস্যা হয়েছে।");
      }

      onSuccess();
      onClose();
      setName("");
      setSku("");
      setBarcode("");
      setPrice("");
      setComparePrice("");
      setDescription("");
      setSelectedCategory("");
      setImageFile(null);
      setImagePreview(null);
    } catch (err: any) {
      setError(err.message || "Failed to create product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-sm animate-fadeIn">
      <div
        className={`w-full max-w-xl max-h-[92vh] flex flex-col rounded-3xl shadow-2xl border transition-all ${
          isLight
            ? "bg-white border-slate-200 text-slate-900"
            : "bg-slate-900 border-slate-800 text-white"
        }`}
      >
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-200 dark:border-slate-800 shrink-0">
          <div className="flex items-center gap-3">
            <div
              style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}
              className="p-2.5 rounded-2xl shrink-0"
            >
              <Package className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-black">নতুন ব্রাঞ্চ প্রোডাক্ট যুক্ত করুন</h2>
              <p className="text-xs text-slate-400">Default থিম আউটলেটের জন্য প্রোডাক্ট স্টক করুন</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form
          id="add-product-form"
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {error && (
            <div className="p-3.5 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-500 text-xs font-bold leading-relaxed">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-black uppercase tracking-wider mb-1.5 text-slate-400">
              পণ্যের নাম <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="যেমন: চকলেট কেক"
              className={`w-full px-3.5 py-2.5 rounded-2xl text-xs font-bold border outline-none ${
                isLight ? "bg-slate-50 border-slate-300 text-slate-900" : "bg-slate-950 border-slate-800 text-white"
              }`}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-black uppercase tracking-wider mb-1.5 text-slate-400">
                বিক্রয় মূল্য (৳) <span className="text-rose-500">*</span>
              </label>
              <div className="relative flex items-center">
                <DollarSign className="w-4 h-4 absolute left-3 text-emerald-400" />
                <input
                  type="number"
                  step="0.01"
                  required
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="৫৫০"
                  className={`w-full pl-9 pr-3 py-2.5 rounded-2xl text-xs font-mono font-bold border outline-none ${
                    isLight ? "bg-slate-50 border-slate-300 text-slate-900" : "bg-slate-950 border-slate-800 text-white"
                  }`}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-black uppercase tracking-wider mb-1.5 text-slate-400">
                আগের মূল্য (৳)
              </label>
              <input
                type="number"
                step="0.01"
                value={comparePrice}
                onChange={(e) => setComparePrice(e.target.value)}
                placeholder="৬০০"
                className={`w-full px-3.5 py-2.5 rounded-2xl text-xs font-mono font-bold border outline-none ${
                  isLight ? "bg-slate-50 border-slate-300 text-slate-900" : "bg-slate-950 border-slate-800 text-white"
                }`}
              />
            </div>

            <div>
              <label className="block text-xs font-black uppercase tracking-wider mb-1.5 text-slate-400">
                স্টক পরিমাণ <span className="text-rose-500">*</span>
              </label>
              <input
                type="number"
                required
                value={stockQuantity}
                onChange={(e) => setStockQuantity(e.target.value)}
                placeholder="১০"
                className={`w-full px-3.5 py-2.5 rounded-2xl text-xs font-mono font-bold border outline-none ${
                  isLight ? "bg-slate-50 border-slate-300 text-slate-900" : "bg-slate-950 border-slate-800 text-white"
                }`}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-black uppercase tracking-wider mb-1.5 text-slate-400">
                ক্যাটাগরি নির্বাচন
              </label>
              <div className="relative flex items-center">
                <Layers className="w-4 h-4 absolute left-3.5 text-slate-400 pointer-events-none" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2.5 rounded-2xl text-xs font-bold border outline-none appearance-none cursor-pointer ${
                    isLight ? "bg-slate-50 border-slate-300 text-slate-900" : "bg-slate-950 border-slate-800 text-white"
                  }`}
                >
                  <option value="">ক্যাটাগরি নেই (সাধারণ)</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-black uppercase tracking-wider mb-1.5 text-slate-400">
                পরিমাপের একক (Unit)
              </label>
              <input
                type="text"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                placeholder="Piece, Kg, Liter"
                className={`w-full px-3.5 py-2.5 rounded-2xl text-xs font-bold border outline-none ${
                  isLight ? "bg-slate-50 border-slate-300 text-slate-900" : "bg-slate-950 border-slate-800 text-white"
                }`}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-black uppercase tracking-wider mb-1.5 text-slate-400">
                SKU কোড
              </label>
              <input
                type="text"
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                placeholder="অটো জেনারেট হবে খালি রাখলে"
                className={`w-full px-3.5 py-2.5 rounded-2xl text-xs font-mono font-bold border outline-none ${
                  isLight ? "bg-slate-50 border-slate-300 text-slate-900" : "bg-slate-950 border-slate-800 text-white"
                }`}
              />
            </div>

            <div>
              <label className="block text-xs font-black uppercase tracking-wider mb-1.5 text-slate-400">
                বারকোড
              </label>
              <div className="relative flex items-center">
                <Barcode className="w-4 h-4 absolute left-3.5 text-slate-400" />
                <input
                  type="text"
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  placeholder="POS বারকোড নম্বর"
                  className={`w-full pl-10 pr-4 py-2.5 rounded-2xl text-xs font-mono font-bold border outline-none ${
                    isLight ? "bg-slate-50 border-slate-300 text-slate-900" : "bg-slate-950 border-slate-800 text-white"
                  }`}
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-black uppercase tracking-wider mb-1.5 text-slate-400">
              পণ্যের ছবি
            </label>
            <div className="relative border-2 border-dashed border-slate-800 hover:border-emerald-500/50 rounded-2xl p-4 text-center transition-colors">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              {imagePreview ? (
                <div className="relative w-full h-32 rounded-xl overflow-hidden">
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-black/70 text-[10px] text-emerald-400 font-bold">
                    ✓ ছবি সিলেক্ট হয়েছে
                  </span>
                </div>
              ) : (
                <div className="space-y-1.5 py-2">
                  <Upload className="w-6 h-6 mx-auto text-slate-500" />
                  <p className="text-xs font-bold text-slate-400">ছবি আপলোড করতে ক্লিক করুন</p>
                  <p className="text-[10px] text-slate-600">PNG, JPG, WEBP (Square Recommended)</p>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-black uppercase tracking-wider mb-1.5 text-slate-400">
              পণ্যের বিবরণ
            </label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="পণ্যের গুণাগুণ ও সাইজ সম্পর্কিত তথ্য..."
              className={`w-full p-3 rounded-2xl text-xs font-bold border outline-none resize-none ${
                isLight ? "bg-slate-50 border-slate-300 text-slate-900" : "bg-slate-950 border-slate-800 text-white"
              }`}
            />
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 p-5 sm:p-6 border-t border-slate-200 dark:border-slate-800 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-2xl text-xs font-bold text-slate-400 hover:bg-slate-800 hover:text-white transition-colors cursor-pointer"
          >
            বাতিল
          </button>
          <button
            type="submit"
            form="add-product-form"
            disabled={loading}
            style={{ backgroundColor: primaryColor }}
            className="flex items-center gap-2 px-6 py-2.5 rounded-2xl text-xs font-black text-white shadow-lg hover:opacity-90 transition-all disabled:opacity-50 cursor-pointer"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>যুক্ত হচ্ছে...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                <span>পণ্য সেভ করুন</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}